import { useCallback, useState } from 'react';

import { request } from '../../shared/api';
import { EmptyState, ListSkeleton, renderFailure } from '../../shared/states';
import { SummaryRow, type Summary } from '../../shared/SummaryRow';
import { ago } from '../../shared/time';
import { useOpened } from '../../shared/use-opened';
import { usePage } from '../../shared/use-page';
import { ListingScreen } from '../listing/ListingScreen';
import { useUpgrade } from '../../shared/upgrade';

/**
 * История уведомлений (T11.10, `05_API.md` §9).
 *
 * Внутри профиля, а не вкладкой: список открывают, когда уведомление
 * потерялось в переписке, — это раз в месяц, а место в главной
 * навигации стоит пятой части экрана у тех вкладок, что работают
 * ежедневно (`09_UI_UX.md` §3).
 *
 * ## «Прочитать всё» отмечает по моменту, а не по перечню
 *
 * Сервер принимает два способа: `ids` — то, что человек видел, и
 * `before` — всё до момента. Кнопка «прочитать всё» означает второе:
 * перечислять поимённо тысячу накопившихся уведомлений клиент не
 * станет, а отметить только показанную страницу значило бы обмануть
 * подпись на кнопке.
 *
 * Моментом берётся первое уведомление в списке — самое свежее. Взять
 * «сейчас» значило бы отметить прочитанным и то, что придёт, пока
 * запрос идёт по сети.
 */

interface Notification {
  readonly notification_id: string | number;
  readonly type: string;
  readonly created_at: string;
  readonly is_read: boolean;
  readonly subscription: { id: string | number; name: string };
  readonly listing: Summary;
}

/** Сколько уведомлений за раз. */
const PAGE = 20;

/**
 * Повод уведомления словами.
 *
 * Коды те же, что в `notifications_type_check`. Незнакомый показывается
 * общим словом: типы добавляются на сервере раньше, чем здесь, и пустая
 * строка выглядела бы поломкой.
 */
const REASONS: Record<string, string> = {
  new_listing: 'Новое объявление',
  price_drop: 'Цена снижена',
  republished: 'Объявление переопубликовано',
  attributes_changed: 'Объявление изменено',
};

function reason(type: string): string {
  return REASONS[type] ?? 'Событие по подписке';
}

/**
 * Адрес перечня уведомлений — с отбором по подписке или без.
 *
 * Вынесено из тела экрана ради проверки: собери мы адрес неверно —
 * лишним знаком вопроса, незакодированным значением, — и человек
 * увидел бы не пустоту, а **чужой** перечень: сервер просто не заметил
 * бы отбора и отдал все уведомления. Ошибка, которую глазами не
 * отличить от исправной работы.
 */
export function notificationsPath(subscriptionId?: string | undefined): string {
  if (subscriptionId === undefined) return '/v1/notifications';

  return `/v1/notifications?subscription=${encodeURIComponent(subscriptionId)}`;
}

export function NotificationsScreen({
  onBack,
  /**
   * Показывать только уведомления этой подписки.
   *
   * Пусто — все, как на экране «Уведомления» в профиле. Заведено ради
   * «Моих радаров»: там перечень подписок, и провалиться в радар,
   * чтобы посмотреть его улов, было некуда. Экран для этого годится
   * целиком — и разбор события, и переход в объявление, и отметка
   * прочитанным здесь уже есть, а второй такой же разошёлся бы с этим
   * при первой же правке.
   */
  subscriptionId,
  /** Подпись кнопки возврата: экран открывается из двух мест. */
  backLabel = '← В профиль',
}: {
  onBack: () => void;
  subscriptionId?: string | undefined;
  backLabel?: string;
}): React.JSX.Element {
  const page = usePage<Notification>(notificationsPath(subscriptionId), PAGE);
  const { opened, open, back, flow } = useOpened();
  const [marking, setMarking] = useState(false);
  const { drop, items, replace } = page;

  const remove = useCallback(
    (notificationId: string | number) => {
      const id = String(notificationId);

      request(`/v1/notifications/${id}`, { method: 'DELETE' })
        .then(() => {
          drop((one) => String(one.notification_id) === id);
        })
        .catch(() => {
          // Строка остаётся: убрать её, не зная, убрал ли сервер,
          // значит показать список, которого нет
        });
    },
    [drop],
  );

  const markAll = useCallback(() => {
    const newest = items[0];

    if (newest === undefined) return;

    setMarking(true);
    request('/v1/notifications/mark-read', {
      method: 'POST',
      body: { before: newest.created_at },
    })
      .then(() => {
        // Отмечается ровно то, что отметил сервер: всё до момента, а
        // подгруженные страницы старше него все до одной
        replace((one) => ({ ...one, is_read: true }));
      })
      .catch(() => undefined)
      .finally(() => {
        setMarking(false);
      });
  }, [items, replace]);

  const unread = items.filter((one) => !one.is_read).length;

  return (
    <>
      {opened === null ? null : <ListingScreen listingId={opened} onBack={back} />}

      <div className={flow}>
        <button type="button" className="back" onClick={onBack}>
          {backLabel}
        </button>

        {unread === 0 ? null : (
          <button type="button" className="button" disabled={marking} onClick={markAll}>
            {marking ? 'Отмечаю…' : `Прочитать всё (${String(unread)})`}
          </button>
        )}

        <Body page={page} onOpen={open} onRemove={remove} />
      </div>
    </>
  );
}

function Body({
  page,
  onOpen,
  onRemove,
}: {
  page: ReturnType<typeof usePage<Notification>>;
  onOpen: (listingId: string | number) => void;
  onRemove: (notificationId: string | number) => void;
}): React.JSX.Element {
  const onUpgrade = useUpgrade();

  if (page.state === 'loading') return <ListSkeleton rows={3} />;

  if (page.error !== null && page.items.length === 0) {
    return <>{renderFailure(page.error, { onRetry: page.reload, onUpgrade })}</>;
  }

  if (page.items.length === 0) {
    return (
      <EmptyState
        title="Уведомлений ещё не было"
        hint="Как только по вашим подпискам появится подходящая машина, она придёт в чат и останется здесь."
      />
    );
  }

  return (
    <>
      {page.items.map((one) => (
        <div className="notification" key={String(one.notification_id)}>
          <p className={one.is_read ? 'notification__head' : 'notification__head is-unread'}>
            {one.is_read ? '' : '● '}
            {reason(one.type)} · {one.subscription.name} · {ago(one.created_at)}
          </p>

          <SummaryRow
            listing={one.listing}
            onOpen={onOpen}
            action={{
              label: 'Удалить',
              onClick: () => {
                onRemove(one.notification_id);
              },
            }}
          />
        </div>
      ))}

      {page.hasMore ? (
        <button
          type="button"
          className="button"
          disabled={page.state === 'more'}
          onClick={page.more}
        >
          {page.state === 'more' ? 'Загружаю…' : 'Показать ещё'}
        </button>
      ) : null}
    </>
  );
}
